import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { money, variantLabel, categoryLabel } from "@/lib/constants";
import PosterCard from "@/components/PosterCard";
import AdBanner from "@/components/AdBanner";
import { Images, DollarSign, TrendingUp, MapPin, Layers, Frame, Plus, ArrowRight } from "lucide-react";

function Stat({ icon: Icon, label, value, accent = "text-acid" }) {
  return (
    <div className="bg-surface border border-stroke p-5 hard-shadow">
      <Icon className={accent} size={22} />
      <div className="font-display text-4xl text-paper mt-3 leading-none">{value}</div>
      <div className="font-mono text-[10px] uppercase tracking-widest text-muted-gray mt-1">{label}</div>
    </div>
  );
}

export default function Dashboard() {
  const [stats, setStats] = useState(null);
  const [recent, setRecent] = useState([]);
  const navigate = useNavigate();

  useEffect(() => {
    api.get("/stats").then((r) => setStats(r.data));
    api.get("/posters").then((r) => setRecent(r.data.slice(0, 8)));
  }, []); // run once on mount; api + state setters are stable

  return (
    <div className="p-6 lg:p-10 max-w-7xl">
      <AdBanner className="mb-6" />
      <div className="flex items-end justify-between flex-wrap gap-4 mb-8">
        <div>
          <div className="font-mono text-xs uppercase tracking-[0.3em] text-acid">Overview</div>
          <h1 className="font-display text-5xl lg:text-6xl text-paper leading-none mt-1">DASHBOARD</h1>
        </div>
        <Link to="/app/collection" data-testid="dashboard-add-link"
          className="inline-flex items-center gap-2 font-mono text-xs uppercase tracking-wider bg-acid text-ink font-bold px-5 py-3 hard-shadow">
          <Plus size={16} /> Add Poster
        </Link>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <Stat icon={Images} label="Posters Owned" value={stats?.total_posters ?? "—"} />
        <Stat icon={DollarSign} label="Total Spent" value={stats ? money(stats.total_spent) : "—"} />
        <Stat icon={Layers} label="Market Value" value={stats ? money(stats.total_value) : "—"} accent="text-cyan" />
        <Stat icon={TrendingUp} label="Net Gain" value={stats ? money(stats.gain) : "—"}
          accent={stats && stats.gain < 0 ? "text-magenta" : "text-acid"} />
        <Stat icon={MapPin} label="Cities" value={stats?.cities ?? "—"} accent="text-cyan" />
        <Stat icon={MapPin} label="Shows Logged" value={stats?.shows ?? "—"} accent="text-gold" />
        <Stat icon={Frame} label="Framed" value={stats?.framed ?? "—"} accent="text-cyan" />
        <div className="bg-surface border border-stroke p-5">
          <div className="font-mono text-[10px] uppercase tracking-widest text-muted-gray mb-2">Variants</div>
          <div className="flex flex-wrap gap-1.5">
            {stats && Object.keys(stats.variants).length ? (
              Object.entries(stats.variants).map(([k, v]) => (
                <span key={k} className="font-mono text-[10px] px-2 py-1 border border-stroke text-paper">
                  {variantLabel(k)} · {v}
                </span>
              ))
            ) : (
              <span className="font-mono text-xs text-muted-gray">—</span>
            )}
          </div>
        </div>
      </div>

      {stats && stats.categories && Object.keys(stats.categories).length > 0 && (
        <div className="mt-6 bg-surface border border-stroke p-5">
          <div className="font-mono text-[10px] uppercase tracking-widest text-muted-gray mb-3">Collection by Type</div>
          <div className="flex flex-wrap gap-2">
            {Object.entries(stats.categories).map(([k, v]) => (
              <span key={k} className="font-mono text-xs px-3 py-1.5 border border-stroke text-paper flex items-center gap-2">
                {categoryLabel(k)} <span className="text-acid font-bold">{v}</span>
              </span>
            ))}
          </div>
        </div>
      )}

      {stats?.top_roi && (
        <div className="mt-6 bg-surface border border-acid/40 p-6 hard-shadow">
          <div className="font-mono text-[10px] uppercase tracking-widest text-acid mb-2">Top ROI Poster</div>
          <div className="flex items-center justify-between flex-wrap gap-2">
            <div>
              <div className="font-display text-3xl text-paper">{stats.top_roi.title}</div>
              <div className="font-mono text-xs text-muted-gray">{stats.top_roi.band}</div>
            </div>
            <div className="text-right">
              <div className="font-mono text-2xl text-acid font-bold">+{money(stats.top_roi.gain)}</div>
              <div className="font-mono text-[11px] text-muted-gray">
                {money(stats.top_roi.purchase_price)} → {money(stats.top_roi.market_value)}
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="mt-10 flex items-center justify-between">
        <h2 className="font-display text-3xl text-paper">Recent Additions</h2>
        <Link to="/app/collection" className="font-mono text-xs uppercase tracking-wider text-muted-gray hover:text-acid flex items-center gap-1">
          View All <ArrowRight size={14} />
        </Link>
      </div>

      {recent.length === 0 ? (
        <div className="mt-6 border border-dashed border-stroke p-12 text-center">
          <p className="font-mono text-sm text-muted-gray">No posters yet. Start building your vault.</p>
          <Link to="/app/collection" className="inline-block mt-4 font-mono text-xs uppercase bg-acid text-ink font-bold px-5 py-3">Add your first poster</Link>
        </div>
      ) : (
        <div className="mt-6 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
          {recent.map((p, i) => (
            <PosterCard key={p.id} poster={p} index={i} onClick={() => navigate("/app/collection")} />
          ))}
        </div>
      )}
    </div>
  );
}
